/**
 * focus.ts — the dirty-state veto.
 *
 * A Brand Bible panel holds the operator's edits in a local buffer until they
 * press Save. If an agent writes the same row while that buffer is open, one of
 * two silent losses happens. The operator's later Save overwrites the agent's
 * row, or the agent's write is overwritten the moment the operator saves. This
 * module makes that collision visible so a command can refuse instead.
 *
 * ## The one architectural rule
 *
 * A page publishes a VALUE, never a handler. `AgentFocus` is plain data. It is
 * encoded, re-parsed and frozen on the way in, so what the runtime reads cannot
 * be mutated behind its back, cannot close over a component's render scope, and
 * cannot go stale when a component unmounts without cleaning up. A contributed
 * `isDirty: () => boolean` would reintroduce all three problems, which is why
 * `BbSectionSaveRegistration` (a handler registry) is deliberately NOT what the
 * runtime reads. The page converts handlers to a value once, in React, where
 * the lifetime is already correct.
 *
 * Intended call site, inside BrandBiblePage, where the registry hooks already
 * live.
 *
 *   const letters = useDirtyLetters();
 *   const registry = useBbSectionSaveRegistry();
 *   useAgentFocus({
 *     tenant,
 *     surface: 'Brand Bible',
 *     dirty: letters.map((s) => ({
 *       section: s,
 *       rowId: rowIdForSection(s) ?? null,
 *       changeCount: registry.changeCount(s),
 *     })),
 *   });
 *
 * ## The one policy rule
 *
 * This is a veto and nothing else. There is no discard flag, no agent-triggered
 * save, no "force" argument and no expiry that would quietly downgrade a veto to
 * a pass. A conflict produces a `Refusal` that names the section and hands the
 * problem back to the human. The agent's only path forward is the operator
 * saving or discarding their own work in the UI.
 *
 * ## Absence is UNKNOWN
 *
 * A page that never publishes is not clean, it is unread. Every read path
 * distinguishes `clear`, `conflict` and `unknown`, and `unknown` is meant to
 * refuse. `ReadDeps.dirtySections` carries all three states, so
 * `dirtySectionsFor` is a straight translation with nothing lost. It used to
 * lossy-encode unknown as an all-nine sentinel set, which was the one seam in
 * this module a caller could read wrong.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react';
import {
  refuse,
  type BbLetter,
  type CommandName,
  type DirtyState,
  type EditorSection,
  type PlannerSection,
  type Refusal,
  type Section,
  type TenantId,
} from './contract';

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

/**
 * Canonical order, A through I. There is no J.
 *
 * Deliberately local rather than imported from `@/lib/bbSectionMap`, because the
 * WebMCP surface must not gain a dependency on page-navigation code. The pair of
 * assertions below makes drift from `BbLetter` a compile error in both
 * directions, so this cannot silently fall out of step with the contract.
 */
export const BB_SECTIONS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I'] as const;

type ListedSection = (typeof BB_SECTIONS)[number];
const _everyListedSectionIsALetter: BbLetter = null as unknown as ListedSection;
const _everyLetterIsListed: ListedSection = null as unknown as BbLetter;
void _everyListedSectionIsALetter;
void _everyLetterIsListed;

/**
 * Content planner surfaces, the second family of sections.
 *
 * Kept beside BB_SECTIONS with the same pair of drift assertions, because the
 * veto fails CLOSED on an unrecognised section: if a planner view published a
 * verdict this set did not list, `isSection` would drop the row, the verdict
 * would read `unknown`, and EVERY planner write would be refused with no
 * obvious cause. That failure is silent at the type level, which is why the
 * runtime set has to be widened in step with the type and asserted both ways.
 */
export const PLANNER_SECTIONS = ['calendar', 'feed', 'table'] as const;

type ListedPlannerSection = (typeof PLANNER_SECTIONS)[number];
const _everyListedPlannerSectionIsOne: PlannerSection = null as unknown as ListedPlannerSection;
const _everyPlannerSectionIsListed: ListedPlannerSection = null as unknown as PlannerSection;
void _everyListedPlannerSectionIsOne;
void _everyPlannerSectionIsListed;

/**
 * Blog editor surfaces, the third family. One member, because the editor is a
 * single page acting on one live document. Same pair of drift assertions as the
 * others: the veto fails CLOSED on an unrecognised section, so this set has to
 * be widened in step with `EditorSection` and asserted both ways.
 */
export const EDITOR_SECTIONS = ['editor', 'editor-meta'] as const;

type ListedEditorSection = (typeof EDITOR_SECTIONS)[number];
const _everyListedEditorSectionIsOne: EditorSection = null as unknown as ListedEditorSection;
const _everyEditorSectionIsListed: ListedEditorSection = null as unknown as EditorSection;
void _everyListedEditorSectionIsOne;
void _everyEditorSectionIsListed;

const SECTION_SET: ReadonlySet<string> = new Set<string>([
  ...BB_SECTIONS,
  ...PLANNER_SECTIONS,
  ...EDITOR_SECTIONS,
]);

function isSection(value: unknown): value is Section {
  return typeof value === 'string' && SECTION_SET.has(value);
}

// ---------------------------------------------------------------------------
// The published value
// ---------------------------------------------------------------------------

/** One unsaved edit the operator is holding. Data, not a callback. */
export interface DirtyEdit {
  readonly section: Section;
  /**
   * The row the buffer will write to, when the panel knows it. Null while the
   * row does not exist yet (a first save creates it) or when the panel cannot
   * name it. Null widens the veto rather than narrowing it. See `overlaps`.
   */
  readonly rowId: string | null;
  /**
   * Buffered edit count, used only in the refusal wording. Omitted means one
   * undifferentiated change, matching the save registry's own rule.
   */
  readonly changeCount?: number;
}

/** The snapshot the runtime reads. Frozen, and owned by exactly one page. */
export interface AgentFocus {
  /**
   * The client the publishing page is showing. Null when no client is selected,
   * in which case every query resolves to `unknown` rather than to `clear`.
   */
  readonly tenant: TenantId | null;
  /** Human-readable screen name, used verbatim in the refusal text. */
  readonly surface: string;
  readonly dirty: readonly DirtyEdit[];
  /**
   * False when the publish carried an entry this module could not understand.
   * A partially-understood publish can prove a conflict but can never prove the
   * absence of one, so it degrades to `unknown` instead of to `clear`.
   */
  readonly trusted: boolean;
  /**
   * Epoch ms of the publish. Diagnostics only. There is deliberately no
   * staleness timeout, because a timeout turns a veto into a pass without
   * anybody deciding to, which is the exact failure this module exists to stop.
   */
  readonly publishedAt: number;
}

export type AgentFocusState =
  | { readonly published: false }
  | { readonly published: true; readonly focus: AgentFocus };

// ---------------------------------------------------------------------------
// Encode and decode
// ---------------------------------------------------------------------------

interface DecodedDirty {
  readonly trusted: boolean;
  readonly edits: readonly DirtyEdit[];
}

interface WireEdit {
  readonly s: string;
  readonly r: string | null;
  readonly c: number;
}

/**
 * Renders the caller's array to a canonical string. Two things fall out of this.
 * The string is the effect dependency, so an inline array literal at the call
 * site does not republish on every render, and re-parsing it produces a copy the
 * caller no longer holds a reference to.
 *
 * Sorted by section then row, so a page that reorders its own list without
 * changing its contents does not count as a change.
 */
function encodeDirty(dirty: readonly DirtyEdit[]): string {
  let trusted = true;
  const wire: WireEdit[] = [];

  for (const edit of dirty) {
    if (!edit || !isSection(edit.section)) {
      trusted = false;
      continue;
    }
    const rowId =
      typeof edit.rowId === 'string' && edit.rowId.length > 0 ? edit.rowId : null;
    const raw = edit.changeCount;
    const changeCount =
      typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 1;
    wire.push({ s: edit.section, r: rowId, c: changeCount });
  }

  wire.sort((a, b) => (a.s === b.s ? (a.r ?? '').localeCompare(b.r ?? '') : a.s < b.s ? -1 : 1));
  return JSON.stringify({ t: trusted, e: wire });
}

function decodeDirty(encoded: string): DecodedDirty {
  let parsed: { t?: unknown; e?: unknown };
  try {
    parsed = JSON.parse(encoded) as { t?: unknown; e?: unknown };
  } catch {
    // Unreachable from encodeDirty. Fail closed rather than throw inside an
    // effect, which React would surface as a page crash.
    return { trusted: false, edits: [] };
  }

  const rows = Array.isArray(parsed.e) ? (parsed.e as WireEdit[]) : [];
  const edits = rows
    .filter((row) => isSection(row.s))
    .map((row) =>
      Object.freeze<DirtyEdit>({
        section: row.s as Section,
        rowId: row.r,
        changeCount: row.c,
      }),
    );

  return {
    trusted: parsed.t === true && edits.length === rows.length,
    edits: Object.freeze(edits),
  };
}

// ---------------------------------------------------------------------------
// The slot
// ---------------------------------------------------------------------------

/**
 * One slot, because the app shows one page at a time. Ownership is an opaque
 * token minted per mounted component, not the value itself. The value is
 * structurally equal across renders but a fresh object each time, so comparing
 * the value would make an unmount look like it belonged to somebody else.
 */
interface FocusSlot {
  readonly owner: object;
  readonly focus: AgentFocus;
}

let slot: FocusSlot | null = null;

const UNPUBLISHED: AgentFocusState = Object.freeze({ published: false as const });
let cachedState: AgentFocusState = UNPUBLISHED;

const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((listener) => listener());
}

/**
 * Takes the slot for `owner`. Idempotent by construction. A republish with the
 * same owner replaces the value, and a republish from a different owner takes
 * ownership, which is what a route transition that mounts before it unmounts
 * looks like.
 */
export function publishAgentFocus(owner: object, focus: AgentFocus): void {
  slot = { owner, focus: Object.freeze(focus) };
  cachedState = Object.freeze({ published: true as const, focus: slot.focus });
  notify();
}

/**
 * Releases the slot only if `owner` still holds it. This is the identity-token
 * compare from `prePublishGate.registerPrePublishConfirm`, and it is what makes
 * the teardown correct in the two cases that matter. React StrictMode's
 * mount-unmount-mount keeps the same ref, so the release and the republish
 * cancel out. A route transition that mounts the next page before unmounting the
 * previous one leaves the slot with the new owner, so the old page's cleanup
 * finds a token that is not its own and does nothing.
 */
export function releaseAgentFocus(owner: object): void {
  if (!slot || slot.owner !== owner) return;
  slot = null;
  cachedState = UNPUBLISHED;
  notify();
}

/**
 * Imperative read for the WebMCP runtime, which runs on a Chrome tool call and
 * is not inside the React tree. Same shape as `useTenantStore.getState()`, and
 * exposed the same way, as a property on the hook.
 */
function readAgentFocus(): AgentFocusState {
  return cachedState;
}

export function subscribeAgentFocus(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// ---------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------

export interface AgentFocusInput {
  readonly tenant: TenantId | null;
  readonly surface: string;
  readonly dirty: readonly DirtyEdit[];
  /**
   * Defaults true. Set false for a surface that is mounted but not the one the
   * operator is looking at. A disabled surface releases the slot if it holds it,
   * which returns the runtime to `unknown`, not to `clear`.
   */
  readonly enabled?: boolean;
}

interface UseAgentFocus {
  (input: AgentFocusInput): void;
  /** Non-React read. See `readAgentFocus`. */
  getState: () => AgentFocusState;
}

/**
 * Publishes what this page currently has unsaved.
 *
 * Safe to call unconditionally, safe under StrictMode's double mount, and a
 * no-op when nothing about the value changed.
 */
export const useAgentFocus: UseAgentFocus = Object.assign(
  function useAgentFocus(input: AgentFocusInput): void {
    const { tenant, surface, dirty, enabled = true } = input;

    // Stable per mounted component, lazily minted. Same shape the Brand Bible
    // save provider uses for its per-render registry.
    const ownerRef = useRef<object | null>(null);
    if (ownerRef.current === null) ownerRef.current = { surface: 'agent-focus' };
    const owner = ownerRef.current;

    // Every dependency below is a primitive, so the effect body can rebuild the
    // exact value it was scheduled for. There is no captured object and
    // therefore no stale closure to reason about.
    const encoded = encodeDirty(dirty);

    useEffect(() => {
      if (!enabled) {
        releaseAgentFocus(owner);
        return;
      }
      const decoded = decodeDirty(encoded);
      publishAgentFocus(owner, {
        tenant,
        surface,
        dirty: decoded.edits,
        trusted: decoded.trusted,
        publishedAt: Date.now(),
      });
    }, [owner, enabled, tenant, surface, encoded]);

    // Teardown is its own effect keyed only on the token, so a value change
    // never opens a window in which the slot is momentarily empty.
    useEffect(() => () => releaseAgentFocus(owner), [owner]);
  },
  { getState: readAgentFocus },
);

/** Reactive read, for UI that wants to show the operator why an agent stopped. */
export function useAgentFocusState(): AgentFocusState {
  return useSyncExternalStore(subscribeAgentFocus, readAgentFocus, readAgentFocus);
}

// ---------------------------------------------------------------------------
// The verdict
// ---------------------------------------------------------------------------

export interface FocusQuery {
  readonly tenant: TenantId;
  readonly section: Section;
  /** The row the write targets. Null or omitted when the write creates a row. */
  readonly rowId?: string | null;
}

export type AgentFocusVerdict =
  /** Positively known to hold nothing unsaved for this tenant and section. */
  | { readonly kind: 'clear' }
  /** Known conflict. The runtime must refuse. */
  | {
      readonly kind: 'conflict';
      readonly conflicts: readonly DirtyEdit[];
      readonly because: string;
    }
  /** Nothing published, a different client, or a publish we could not fully
   *  read. Not clean. The runtime must refuse. */
  | { readonly kind: 'unknown'; readonly because: string };

/**
 * True when a buffered edit could be writing the same row as `query`.
 *
 * Row identity only ever narrows, and only when both sides know it. If either
 * side is null we cannot prove the two are different rows, so the section-level
 * match stands and the veto fires.
 */
function overlaps(edit: DirtyEdit, query: FocusQuery): boolean {
  if (edit.section !== query.section) return false;
  const target = query.rowId ?? null;
  if (target === null || edit.rowId === null) return true;
  return edit.rowId === target;
}

function describe(edits: readonly DirtyEdit[], surface: string, tenant: TenantId): string {
  const parts = edits.map((edit) => {
    const n = edit.changeCount ?? 1;
    return `section ${edit.section} (${n} unsaved change${n === 1 ? '' : 's'})`;
  });
  return (
    `You have ${parts.join(' and ')} open in ${surface} for ${tenant}. ` +
    'Save or discard them yourself, then ask again. ' +
    'This tool cannot save, discard, or work around your local edits.'
  );
}

/**
 * The whole veto, in one function.
 *
 * Keyed on tenant first, then section, then row, so an unsaved section A on one
 * client cannot block a write to section B on another. A tenant mismatch is
 * `unknown` rather than `clear`, because a page showing a different client tells
 * us nothing about this one.
 */
export function agentFocusVerdict(query: FocusQuery): AgentFocusVerdict {
  const state = readAgentFocus();

  if (!state.published) {
    return {
      kind: 'unknown',
      because:
        'No open screen has reported whether you have unsaved Brand Bible edits, ' +
        'so this write could overwrite work in your browser. Refusing rather than guessing.',
    };
  }

  const { focus } = state;

  if (focus.tenant === null) {
    return {
      kind: 'unknown',
      because:
        `${focus.surface} has no client selected, so it cannot say whether ${query.tenant} ` +
        'has unsaved edits open. Refusing rather than guessing.',
    };
  }

  if (focus.tenant !== query.tenant) {
    return {
      kind: 'unknown',
      because:
        `${focus.surface} is showing ${focus.tenant}, not ${query.tenant}, so it cannot say ` +
        `whether ${query.tenant} has unsaved edits open. Refusing rather than guessing.`,
    };
  }

  const conflicts = focus.dirty.filter((edit) => overlaps(edit, query));
  if (conflicts.length > 0) {
    return {
      kind: 'conflict',
      conflicts,
      because: describe(conflicts, focus.surface, query.tenant),
    };
  }

  if (!focus.trusted) {
    return {
      kind: 'unknown',
      because:
        `${focus.surface} reported its unsaved edits in a shape this build does not ` +
        'recognise, so a clean result cannot be trusted. Refusing rather than guessing.',
    };
  }

  return { kind: 'clear' };
}

/**
 * The refusal a command returns. Null when the write may proceed.
 *
 * There is no `remedy`, and that omission is load-bearing. `remedy` names a
 * command that would unblock the agent, and by design no such command exists.
 * Only the human can clear this. `retryable` is true because the same call will
 * succeed once they do.
 */
export function agentFocusRefusal(query: FocusQuery): Refusal | null {
  const verdict = agentFocusVerdict(query);
  if (verdict.kind === 'clear') return null;
  return refuse(verdict.because, { retryable: true });
}

/**
 * Convenience for a command that already knows which command name to blame in
 * its own logs. Behaves exactly like `agentFocusRefusal`; the name is carried
 * for telemetry and is never turned into a `remedy`.
 */
export function agentFocusRefusalFor(
  query: FocusQuery,
  _command: CommandName,
): Refusal | null {
  return agentFocusRefusal(query);
}

// ---------------------------------------------------------------------------
// The ReadDeps adapter
// ---------------------------------------------------------------------------

/**
 * Adapts to `ReadDeps.dirtySections()`.
 *
 * A straight translation of `agentFocusVerdict`'s three states onto the
 * contract's three. There is no encoding step and nothing is collapsed, so a
 * caller cannot mistake "we know nothing" for "nothing is dirty".
 *
 * This replaced an `UNKNOWN_SECTIONS` sentinel: unknown used to be returned as
 * a set containing all nine letters, which fails closed only for a caller that
 * knows to identity-compare against the exported sentinel, and reads as "every
 * section is dirty" to one that does not. `DirtyState` makes the distinction a
 * type rather than a convention, and `contract.dirtyVeto` is the one reader.
 */
export function dirtySectionsFor(tenant: TenantId): DirtyState {
  const state = readAgentFocus();

  if (!state.published) {
    return {
      kind: 'unknown',
      because:
        'No open screen has reported whether you have unsaved Brand Bible edits, ' +
        'so this write could overwrite work in your browser. Refusing rather than guessing.',
    };
  }

  const { focus } = state;

  if (focus.tenant === null) {
    return {
      kind: 'unknown',
      because:
        `${focus.surface} has no client selected, so it cannot say whether ${tenant} ` +
        'has unsaved edits open. Refusing rather than guessing.',
    };
  }

  if (focus.tenant !== tenant) {
    return {
      kind: 'unknown',
      because:
        `${focus.surface} is showing ${focus.tenant}, not ${tenant}, so it cannot say ` +
        `whether ${tenant} has unsaved edits open. Refusing rather than guessing.`,
    };
  }

  if (!focus.trusted) {
    return {
      kind: 'unknown',
      because:
        `${focus.surface} reported its unsaved edits in a shape this build does not ` +
        'recognise, so a clean result cannot be trusted. Refusing rather than guessing.',
    };
  }

  if (focus.dirty.length === 0) return { kind: 'clean' };

  return {
    kind: 'dirty',
    sections: new Set<Section>(focus.dirty.map((edit) => edit.section)),
  };
}

/**
 * Test seam. Resets the module to its unpublished state, which is `unknown`.
 * Not exported to the runtime and never called by page code.
 */
export function __resetAgentFocusForTests(): void {
  slot = null;
  cachedState = UNPUBLISHED;
  notify();
}
